import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { DoubleColonFilterGroup, DoubleColonSelection } from './doubleColonFilterUtils';
import styles from './DoubleColonFilters.module.scss';

type DoubleColonFiltersProps = {
  groups: DoubleColonFilterGroup[];
  selection: DoubleColonSelection;
  onSelect: (key: string, tag: string | null) => void;
  /**
   * Whether anything in the row — a selection or the keyword search rendered as children — is
   * narrowing the feed. The owner decides, since the keyword's state lives there; this row only
   * shows the Clear control when there is something to clear.
   */
  isFiltering: boolean;
  onClear: () => void;
  /** Scopes the generated input ids, so two feed blocks on one page keep distinct labels. */
  idPrefix: string;
  /** Rendered as the last control in the row — the feed's keyword search sits here. */
  children?: ReactNode;
};

export function DoubleColonFilters(props: DoubleColonFiltersProps) {
  const { groups, selection, onSelect, isFiltering, onClear, idPrefix, children } = props;
  const { t } = useTranslation();

  if (groups.length === 0) return null;

  return (
    <div className={styles.filters}>
      {groups.map((group, index) => {
        // Indexed rather than keyed on group.key, which is author-supplied and may contain
        // characters that make a poor DOM id.
        const selectId = `${idPrefix}-filter-${index}`;

        return (
          <div key={group.key} className={styles.filter}>
            <label className={styles.label} htmlFor={selectId}>
              {group.key}
            </label>
            <select
              id={selectId}
              className={styles.select}
              value={selection[group.key] ?? ''}
              onChange={(event) => onSelect(group.key, event.target.value || null)}
            >
              <option value="">{t('feed.filterAny')}</option>
              {group.options.map((option) => (
                <option key={option.tag} value={option.tag}>
                  {option.value}
                </option>
              ))}
            </select>
          </div>
        );
      })}

      {children}

      {isFiltering && (
        <ActionButton className={styles.clearButton} type={ButtonType.ACTION_SECONDARY} onClick={onClear}>
          {t('feed.clearFilters')}
        </ActionButton>
      )}
    </div>
  );
}
